import { useState } from "react";
import { Link, createFileRoute, useSearch } from "@tanstack/react-router";

import { ApiError, auth } from "~/lib/api";
import { AuthShell, Field, inputCls, primaryBtn } from "./auth.sign-in";

export const Route = createFileRoute("/auth/reset-password")({
  component: ResetPasswordPage,
  validateSearch: (raw): { token?: string } => ({
    token: typeof raw.token === "string" ? raw.token : undefined,
  }),
});

function ResetPasswordPage() {
  const { token } = useSearch({ from: "/auth/reset-password" });
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!token) {
    return (
      <AuthShell title="Invalid reset link">
        <p className="text-sm text-[color:var(--color-abyssal)]/70">
          This link is missing the reset token. Request a new one from{" "}
          <Link to="/auth/forgot-password" className="text-[color:var(--color-north-star)] underline">
            forgot password
          </Link>
          .
        </p>
      </AuthShell>
    );
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await auth.resetPassword(token, password);
      setDone(true);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not reset password");
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <AuthShell title="Password updated" subtitle="Your previous sessions have been signed out.">
        <p className="text-sm text-[color:var(--color-abyssal)]/70">
          Sign in with your new password to continue.
        </p>
        <div className="mt-6">
          <Link to="/auth/sign-in" className={primaryBtn + " block text-center"}>
            Sign in
          </Link>
        </div>
      </AuthShell>
    );
  }

  return (
    <AuthShell title="Choose a new password">
      <form onSubmit={handleSubmit} className="space-y-4">
        <Field label="New password" hint="Minimum 12 characters.">
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="new-password"
            minLength={12}
            required
            className={inputCls}
          />
        </Field>

        {error ? <p className="text-sm text-[color:var(--color-coral)]">{error}</p> : null}

        <button type="submit" disabled={submitting} className={primaryBtn}>
          {submitting ? "Updating…" : "Update password"}
        </button>
      </form>
    </AuthShell>
  );
}
